import { useState } from "react";
import { Link } from "react-router-dom";
import { FlagTriangleRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtUSD, errMsg } from "@/lib/api";

const inp = "w-full text-xs border border-slate-300 rounded px-2 py-1.5 bg-white outline-none focus:border-sky-500";

export function RaiseIssue({ alert, onChanged }) {
  const w = alert.wire;
  const init = () => ({ title: `AML: ${alert.rule_name} - ${w.counterparty} (${w.country})`, severity: alert.severity, owner: "BSA/AML Officer",
    notes: alert.note?.content || alert.explanation || [`Alert ${alert.id} - ${alert.rule_name} (${alert.rule_code})`, `Wire ${w.id}: ${fmtUSD(w.amount)} to ${w.counterparty} (${w.country}) via ${w.bank}, status ${w.status}`,
      ...(alert.data_points || []).map((d) => `${d.label}: ${d.value}`), "", "Investigator assessment: "].join("\n") });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  if (alert.grc_issue_id)
    return (
      <div className="panel p-3 flex items-center justify-between text-xs" data-testid="alert-linked-issue">
        <span className="text-slate-700 flex items-center gap-1.5"><FlagTriangleRight className="w-3.5 h-3.5 text-red-600" />Linked GRC issue <span className="num text-slate-900">{alert.grc_issue_id}</span></span>
        <Link to="/grc" data-testid="alert-linked-issue-open" className="text-sky-700 underline">Open in GRC</Link>
      </div>
    );
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/grc/alert-issues/${alert.id}`, f); toast.success(`${data.id} raised and linked to ${alert.id}`); setOpen(false); await onChanged(); }
    catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Could not raise issue"); }
    setBusy(false);
  };
  if (String(alert.status).startsWith("Closed")) return <div className="text-[11px] text-slate-500" data-testid="alert-raise-closed">Alert is closed - reopen it to raise a GRC issue.</div>;
  if (!open)
    return <button data-testid="alert-raise-issue-button" onClick={() => { setF(init()); setOpen(true); }} className="w-full text-xs flex items-center justify-center gap-1.5 px-2.5 py-2 rounded border border-red-500/40 text-red-700 hover:bg-red-500/5 transition-colors"><FlagTriangleRight className="w-3.5 h-3.5" />Raise GRC issue from this alert</button>;
  return (
    <div className="panel p-4 space-y-2" data-testid="alert-raise-issue-form">
      <div className="eyebrow">Raise GRC issue · links to {alert.id} / {w.id}</div>
      <input data-testid="raise-issue-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} />
      <div className="flex gap-2">
        <select data-testid="raise-issue-severity" value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} className={inp}>{["High", "Medium", "Low"].map((s) => <option key={s}>{s}</option>)}</select>
        <input data-testid="raise-issue-owner" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} className={inp} />
      </div>
      <textarea data-testid="raise-issue-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Investigation notes…" className={`${inp} h-28 num`} />
      <div className="flex justify-end gap-2">
        <button data-testid="raise-issue-cancel" onClick={() => setOpen(false)} className="text-xs px-2.5 py-1 rounded border border-slate-300 text-slate-600">Cancel</button>
        <button data-testid="raise-issue-submit" disabled={busy || !f.title.trim()} onClick={submit} className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">{busy && <Loader2 className="w-3 h-3 animate-spin" />}Raise issue</button>
      </div>
    </div>
  );
}
